import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';

// Import shared components and your existing admin styles
import AdminSidebar from '../Adminsidebar/AdminSidebar';
import '../../css/bootstrap.min.css';
import '../../css/feathericon.min.css';
import '../../css/font-awesome.min.css';
import '../../css/style.css';
import 'react-calendar/dist/Calendar.css';
// Import the new, redesigned CSS
import './Newdoc.css';

// Import FontAwesome icons for a modern look
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMd, faStethoscope, faMapMarkerAlt, faRupeeSign, faImage, faClock, faPlus, faTrash, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

function NewAddDoctor() {
    // --- STATE MANAGEMENT ---
    const [name, setName] = useState('');
    const [speciality, setSpeciality] = useState('');
    const [address, setAddress] = useState('');
    const [price, setPrice] = useState('');
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
   const [availability, setAvailability] = useState([{ startTime: '09:00', endTime: '17:00', isAvailable: true }]);
     const [availableDates, setAvailableDates] = useState([]);

    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'
    const [loading, setLoading] = useState(false);
    
    const navigate = useNavigate();
   
    
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.hitkarihospital.com:5005';

    // --- FILE HANDLING LOGIC ---
    const handleFileChange = (file) => {
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        } else {
            // Handle non-image file selection if needed
            setImage(null);
            setPreview(null);
        }
    };

    const handleDragEvents = (e, isEntering) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(isEntering);
    };

    const handleDrop = (e) => {
        handleDragEvents(e, false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

   // --- AVAILABILITY LOGIC ---
    const handleAddAvailabilitySlot = () => {
        // === MODIFIED: Add new slots with isAvailable set to true by default ===
        setAvailability([...availability, { startTime: '09:00', endTime: '17:00', isAvailable: true }]);
    };

    const handleRemoveAvailabilitySlot = (index) => {
        setAvailability(availability.filter((_, i) => i !== index));
    };

    const handleAvailabilityChange = (index, field, value) => {
        const newAvailability = [...availability];
        // === MODIFIED: Handle boolean for checkbox and string for time inputs ===
        newAvailability[index][field] = field === 'isAvailable' ? value : String(value);
        setAvailability(newAvailability);
    };
    
    // === NEW: Handler for selecting/deselecting dates on the calendar ===
    const handleDateChange = (date) => {
        setAvailableDates(prevDates => {
            // Check if the date is already selected by comparing date strings
            const isAlreadySelected = prevDates.some(
                d => d.toDateString() === date.toDateString()
            );

            if (isAlreadySelected) {
                // If selected, remove it from the array
                return prevDates.filter(d => d.toDateString() !== date.toDateString());
            } else {
                // If not selected, add it to the array
                return [...prevDates, date];
            }
        });
    };

    // === NEW: Function to style selected dates on the calendar ===
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const isSelected = availableDates.some(
                d => d.toDateString() === date.toDateString()
            );
            if (isSelected) {
                return 'date-selected'; // We will define this CSS class
            }
        }
        return null;
    };

    // --- FORM SUBMISSION ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setLoading(true);

        if (!name || !speciality || !address || !price || !image) {
            setMessage('Please fill all fields and upload an image.');
            setMessageType('error');
            setLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('speciality', speciality);
        formData.append('address', address);
        formData.append('price', price);
        formData.append('image', image);
        formData.append('availability', JSON.stringify(availability));
   const datesToSubmit = availableDates.map(date => date.toISOString());
        formData.append('availableDates', JSON.stringify(datesToSubmit));
        try {
            const response = await axios.post(`${API_BASE_URL}/api/doctor`, formData);
            setMessage('Doctor added successfully!');
            setMessageType('success');
            setTimeout(() => navigate('/doctor'), 2000);
        } catch (err) {
            setMessage('Error adding doctor: ' + (err.response?.data?.error || err.message));
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-wrapper">
            <AdminSidebar />
            <div className="page-wrapper">
                <div className="content container-fluid">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <h3 className="page-title">Add New Doctor</h3>
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><a href="/dash">Dashboard</a></li>
                                    <li className="breadcrumb-item"><a href="/admin/doctor-list">Doctors</a></li>
                                    <li className="breadcrumb-item active">Add Doctor</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    
                    {/* Main Content Area */}
                    <div className="add-doc-page">
                        <form onSubmit={handleSubmit} noValidate>
                            {message && (
                                <div className={`add-doc-alert add-doc-alert-${messageType}`}>
                                    {messageType === 'success' ? <FontAwesomeIcon icon={faCheckCircle} /> : ''} {message}
                                </div>
                            )}

                            <div className="add-doc-form-grid">
                                {/* Left Column */}
                                <div className="add-doc-col">
                                    {/* Basic Info Card */}
                                    <div className="add-doc-card">
                                        <h4 className="add-doc-card-title">Basic Information</h4>
                                        <div className="add-doc-form-group">
                                            <label htmlFor="docName">Doctor Name</label>
                                            <div className="add-doc-input-group">
                                                <FontAwesomeIcon icon={faUserMd} className="add-doc-input-icon" />
                                                <input id="docName" type="text" className="add-doc-input" placeholder="e.g., Dr. John Doe" value={name} onChange={(e) => setName(e.target.value)} required />
                                            </div>
                                        </div>
                                        <div className="add-doc-form-group">
                                            <label htmlFor="docSpeciality">Speciality</label>
                                            <div className="add-doc-input-group">
                                                <FontAwesomeIcon icon={faStethoscope} className="add-doc-input-icon" />
                                                <input id="docSpeciality" type="text" className="add-doc-input" placeholder="e.g., Cardiology" value={speciality} onChange={(e) => setSpeciality(e.target.value)} required />
                                            </div>
                                        </div>
                                        <div className="add-doc-form-group">
                                            <label htmlFor="docAddress">Address</label>
                                            <div className="add-doc-input-group">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="add-doc-input-icon" />
                                                <input id="docAddress" type="text" className="add-doc-input" placeholder="e.g., 123 Health St, Medicity" value={address} onChange={(e) => setAddress(e.target.value)} required />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Professional Details Card */}
                                    <div className="add-doc-card">
                                        <h4 className="add-doc-card-title">Professional Details</h4>
                                        <div className="add-doc-form-group">
                                            <label htmlFor="docFees">Consultation Fees</label>
                                            <div className="add-doc-input-group">
                                                <FontAwesomeIcon icon={faRupeeSign} className="add-doc-input-icon" />
                                                <input id="docFees" type="number" className="add-doc-input" placeholder="e.g., 500" value={price} onChange={(e) => setPrice(e.target.value)} required />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Right Column */}
                                <div className="add-doc-col">
                                    {/* Image Upload Card */}
                                    <div className="add-doc-card">
                                        <h4 className="add-doc-card-title">Profile Picture</h4>
                                        <div className="add-doc-form-group">
                                            <label htmlFor="docImage">Upload Image</label>
                                            <div
                                                className={`add-doc-file-uploader ${isDragging ? 'is-dragging' : ''}`}
                                                onDragEnter={(e) => handleDragEvents(e, true)}
                                                onDragLeave={(e) => handleDragEvents(e, false)}
                                                onDragOver={(e) => handleDragEvents(e, true)}
                                                onDrop={handleDrop}
                                                onClick={() => document.getElementById('docImage').click()}
                                            >
                                                <input id="docImage" type="file" onChange={(e) => handleFileChange(e.target.files[0])} accept="image/*" required hidden />
                                                {preview ? (
                                                    <img src={preview} alt="Doctor Preview" className="add-doc-file-preview" />
                                                ) : (
                                                    <div className="add-doc-upload-placeholder">
                                                        <FontAwesomeIcon icon={faImage} className="add-doc-upload-icon" />
                                                        <p><strong>Drag & drop an image here</strong></p>
                                                        <p>or click to select a file</p>
                                                        <span>PNG, JPG, WEBP up to 5MB</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Availability Card */}
                                    <div className="add-doc-card">
                                        <h4 className="add-doc-card-title">Set Availability</h4>
                                        <div className="add-doc-availability-section">
                                            {availability.map((slot, index) => (
                                                <div key={index} className="add-doc-availability-slot">
                                                    {/* === NEW: Checkbox for availability === */}
                                                    <div className="add-doc-form-group add-doc-availability-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            id={`available-${index}`}
                                                            checked={slot.isAvailable}
                                                            onChange={(e) => handleAvailabilityChange(index, 'isAvailable', e.target.checked)}
                                                        />
                                                        <label htmlFor={`available-${index}`}>Available</label>
                                                    </div>
                                                    <div className="add-doc-time-inputs">
                                                        <div className="add-doc-form-group">
                                                            <label>Start Time</label>
                                                            <input type="time" className="add-doc-input" value={slot.startTime} onChange={(e) => handleAvailabilityChange(index, 'startTime', e.target.value)} required />
                                                        </div>
                                                        <div className="add-doc-form-group">
                                                            <label>End Time</label>
                                                            <input type="time" className="add-doc-input" value={slot.endTime} onChange={(e) => handleAvailabilityChange(index, 'endTime', e.target.value)} required />
                                                        </div>
                                                    </div>
                                                    {availability.length > 1 && (
                                                        <button type="button" className="add-doc-btn-icon" onClick={() => handleRemoveAvailabilitySlot(index)} title="Remove slot">
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button type="button" className="add-doc-btn add-doc-btn-outline" onClick={handleAddAvailabilitySlot}>
                                            <FontAwesomeIcon icon={faPlus} /> Add Time Slot
                                        </button>
                                    </div>
                                     {/* === NEW: Calendar Card === */}
                                    <div className="add-doc-card">
                                        <h4 className="add-doc-card-title">Select Available Dates</h4>
                                        <p className="add-doc-card-subtitle">Click on dates to toggle availability. Selected dates will be highlighted.</p>
                                        <div className="add-doc-calendar-wrapper">
                                            <Calendar
                                                onChange={handleDateChange}
                                                value={availableDates}
                                                tileClassName={tileClassName}
                                                minDate={new Date()} // Prevent selecting past dates
                                                // selectRange={true} allows selecting multiple, but we handle it manually for more control
                                            />
                                        </div>
                                        </div>
                                </div>
                            </div>
                            
                            {/* Form Actions */}
                            <div className="add-doc-form-actions">
                                <button type="button" className="add-doc-btn add-doc-btn-secondary" onClick={() => navigate('/doctor')}>Cancel</button>
                                <button type="submit" className="add-doc-btn add-doc-btn-primary" disabled={loading}>
                                    {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : ''}
                                    {loading ? ' Saving...' : 'Add Doctor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewAddDoctor;